import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import {
  Stethoscope,
  MapPin,
  Shield,
  Building2,
  Activity,
  Sparkles,
  ArrowRight,
  ChevronRight,
  Search,
} from "lucide-react";
import {
  getLandingDirectory,
  getDirectoryTotal,
  type DirectoryCategory,
} from "@/lib/landing-directory";

const SITE = "https://heallexa.com";

const ICONS = {
  Stethoscope,
  MapPin,
  Shield,
  Building2,
  Activity,
  Sparkles,
} as const;

export const Route = createFileRoute("/landing-pages/directory/")({
  head: () => {
    const title = "Landing Pages Directory | Heallexa";
    const description =
      "Browse every Heallexa landing page: specialty + city, specialty + state, insurance + city, conditions, services and more.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `${SITE}/landing-pages/directory` },
      ],
      links: [{ rel: "canonical", href: `${SITE}/landing-pages/directory` }],
    };
  },
  component: LandingDirectoryIndex,
});

function LandingDirectoryIndex() {
  const categories = useMemo(() => getLandingDirectory(), []);
  const total = useMemo(() => getDirectoryTotal(categories), [categories]);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return categories;
    return categories
      .map((c) => ({
        ...c,
        links: c.links.filter((l) => l.label.toLowerCase().includes(q)),
      }))
      .filter((c) => c.links.length > 0);
  }, [categories, q]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
          <nav className="text-sm text-muted-foreground mb-6">
            <Link to="/sitemap" className="hover:text-primary transition-colors">Sitemap</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Landing Pages</span>
          </nav>

          <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Heallexa Directory</p>
          <h1 className="font-display text-[clamp(2.25rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-[-0.04em] text-foreground">
            The full Heallexa
            <br />
            landing page index
          </h1>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-[560px]">
            {total.toLocaleString()} programmatic pages across specialties, cities,
            states, insurance plans, conditions and services — all browseable below.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-[480px]">
            <label className="flex items-center gap-2 h-11 px-3 card-surface-flat rounded-md">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter pages — e.g. 'cardiologist Dallas' or 'Aetna'"
                className="w-full bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground outline-none"
              />
            </label>
          </div>

          {/* TOC chips */}
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className="inline-flex items-center gap-1.5 h-9 px-3 text-[13px] rounded-md border border-border bg-background text-foreground hover:border-primary/40 hover:bg-primary/5 transition-colors"
              >
                {c.title}{" "}
                <span className="text-muted-foreground tabular-nums">
                  {c.links.length.toLocaleString()}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Overview cards */}
      <section className="mx-auto max-w-[1200px] px-6 py-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c, idx) => (
            <Reveal key={c.id} as="div" from="up" delay={Math.min(idx, 8) * 70}>
              <CategoryCard category={c} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Full link sections */}
      <section className="mx-auto max-w-[1200px] px-6 pb-24 space-y-16">
        {filtered.length === 0 && (
          <div className="card-surface p-10 text-center text-[14px] text-muted-foreground rounded-md">
            No pages matched <span className="font-medium text-foreground">"{query}"</span>.
          </div>
        )}

        {filtered.map((c) => (
          <CategorySection key={c.id} category={c} maxVisible={q ? 9999 : 60} />
        ))}
      </section>

      <FooterSection />
    </div>
  );
}

function CategoryCard({ category }: { category: DirectoryCategory }) {
  const Icon = ICONS[category.iconName];
  const sample = category.links.slice(0, 5);
  return (
    <a
      href={`#${category.id}`}
      className="group hover-lift cursor-pointer card-surface p-6 sm:p-7 flex flex-col rounded-md h-full"
    >
      <div className="flex items-start gap-3">
        <span className="h-11 w-11 inline-flex items-center justify-center border border-primary/30 bg-primary/10 text-primary shrink-0 rounded-md">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[16px] font-semibold text-foreground leading-tight">{category.title}</h2>
          <p className="text-[13.5px] text-muted-foreground mt-0.5">
            {category.links.length.toLocaleString()} pages
          </p>
        </div>
      </div>
      <div className="mt-5 flex-1">
        <p className="text-[13.5px] text-muted-foreground">{category.blurb}</p>
        <ul className="mt-3 space-y-1">
          {sample.map((l) => (
            <li key={l.href} className="text-[12px] text-muted-foreground truncate">
              · {l.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4 pt-4 border-t border-border/60 flex items-center justify-end text-[12.5px]">
        <span className="inline-flex items-center gap-1 font-medium text-primary group-hover:gap-2 transition-all">
          Browse all <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </a>
  );
}

function CategorySection({
  category,
  maxVisible,
}: {
  category: DirectoryCategory;
  maxVisible: number;
}) {
  const Icon = ICONS[category.iconName];
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? category.links : category.links.slice(0, maxVisible);
  const canExpand = category.links.length > maxVisible;

  return (
    <section id={category.id} className="scroll-mt-24">
      <div className="flex items-end justify-between gap-6 mb-8">
        <div className="flex items-center gap-4">
          <span className="h-11 w-11 inline-flex items-center justify-center border border-primary/30 bg-primary/10 text-primary shrink-0 rounded-md">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
              {category.title}
            </h2>
            <p className="mt-1 text-[14px] text-muted-foreground max-w-[520px]">{category.blurb}</p>
          </div>
        </div>
        <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground whitespace-nowrap">
          {category.links.length.toLocaleString()} total
        </span>
      </div>

      <div className="card-surface-flat rounded-md p-6">
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
          {shown.map((l) => (
            <li key={l.href} className="min-w-0">
              <a
                href={l.href}
                className="block text-[14px] text-muted-foreground hover:text-primary transition-colors truncate"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-medium text-primary hover:gap-2 transition-all hover:underline"
          >
            {expanded
              ? "Show fewer"
              : `Show all ${category.links.length.toLocaleString()}`}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </section>
  );
}
